import { useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const categories = ["All", "Campus", "Culinary", "Events", "Student Life"];

const galleryImages = [
  { src: "https://customer-assets.emergentagent.com/job_vainavi-college/artifacts/yab3drab_WhatsApp%20Image%202026-03-04%20at%204.01.15%20PM%20%284%29.jpeg", category: "Culinary", alt: "Culinary Arts Students with Chef - Food Carving Display" },
  { src: "https://customer-assets.emergentagent.com/job_vainavi-college/artifacts/4e3jh4f6_WhatsApp%20Image%202026-03-04%20at%204.00.06%20PM.jpeg", category: "Campus", alt: "Professional Culinary Kitchen Lab" },
  { src: "https://customer-assets.emergentagent.com/job_vainavi-college/artifacts/2hjnk04u_WhatsApp%20Image%202026-03-04%20at%204.01.13%20PM.jpeg", category: "Student Life", alt: "F&B Service Training - Restaurant Simulation" },
  { src: "https://customer-assets.emergentagent.com/job_vainavi-college/artifacts/vrnanmho_WhatsApp%20Image%202026-03-04%20at%204.01.14%20PM%20%281%29.jpeg", category: "Culinary", alt: "Bartending Training at Vainavi Bar Lab" },
  { src: "https://customer-assets.emergentagent.com/job_vainavi-college/artifacts/pbcdfpgm_WhatsApp%20Image%202026-03-04%20at%204.00.06%20PM%20%282%29.jpeg", category: "Campus", alt: "Housekeeping Training Suite" },
  { src: "https://customer-assets.emergentagent.com/job_vainavi-college/artifacts/ialfv394_cover%20page.jpg", category: "Events", alt: "Teachers Day Celebration at Vainavi" },
  { src: "https://customer-assets.emergentagent.com/job_vainavi-college/artifacts/yjp5nd00_WhatsApp%20Image%202026-03-04%20at%204.04.56%20PM.jpeg", category: "Events", alt: "Graduation Ceremony at Vainavi" },
  { src: "https://customer-assets.emergentagent.com/job_vainavi-college/artifacts/d3dbw33j_WhatsApp%20Image%202026-03-04%20at%204.04.57%20PM%20%281%29.jpeg", category: "Events", alt: "Award Ceremony with Industry Leaders" },
  { src: "https://customer-assets.emergentagent.com/job_vainavi-college/artifacts/qooq2g5e_WhatsApp%20Image%202026-03-04%20at%204.04.57%20PM.jpeg", category: "Student Life", alt: "Guest Speaker Session at Vainavi" },
  { src: "https://customer-assets.emergentagent.com/job_vainavi-college/artifacts/1zqciifk_WhatsApp%20Image%202026-03-04%20at%204.00.06%20PM%20%281%29.jpeg", category: "Campus", alt: "Restaurant Lab & Fine Dining Setup" },
  { src: "https://images.pexels.com/photos/7437731/pexels-photo-7437731.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", category: "Culinary", alt: "Culinary Students in Professional Kitchen" },
  { src: "https://images.pexels.com/photos/159775/library-la-trobe-study-students-159775.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", category: "Campus", alt: "Library & Study Resources" },
];

// Vary image heights for masonry effect
const heightClasses = ["h-64", "h-80", "h-72", "h-96", "h-64", "h-80", "h-72", "h-64", "h-96", "h-80", "h-72", "h-64"];

export default function Gallery() {
  const [activeFilter, setActiveFilter] = useState("All");
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });

  const filtered = activeFilter === "All"
    ? galleryImages
    : galleryImages.filter((img) => img.category === activeFilter);

  const openLightbox = useCallback((index) => {
    setLightbox({ open: true, index });
    document.body.style.overflow = "hidden";
  }, []);

  const closeLightbox = useCallback(() => {
    setLightbox({ open: false, index: 0 });
    document.body.style.overflow = "";
  }, []);

  const navigate = useCallback((dir) => {
    setLightbox((prev) => ({
      ...prev,
      index: (prev.index + dir + filtered.length) % filtered.length,
    }));
  }, [filtered.length]);

  return (
    <div data-testid="gallery-page">
      {/* Hero Banner */}
      <section className="relative pt-36 pb-20 bg-navy">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
          <p className="font-body text-sm uppercase tracking-[0.3em] text-white/70 mb-3">Gallery</p>
          <h1 data-testid="gallery-hero-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
            Campus Gallery
          </h1>
          <p className="font-body text-lg text-white/80 max-w-2xl">
            Explore life at Vainavi through our lens. From world-class facilities to memorable events.
          </p>
        </div>
      </section>

      {/* Filter & Gallery */}
      <section data-testid="gallery-section" className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          {/* Category Filters */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((cat) => (
              <button
                key={cat}
                data-testid={`gallery-filter-${cat.toLowerCase().replace(/\s/g, '-')}`}
                onClick={() => setActiveFilter(cat)}
                className={`px-5 py-2 font-body text-sm font-semibold uppercase tracking-wider transition-all duration-300 ${
                  activeFilter === cat
                    ? "bg-crimson text-white"
                    : "bg-slate-100 text-navy hover:bg-crimson hover:text-white"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Masonry Grid */}
          <div className="masonry-grid">
            {filtered.map((img, i) => (
              <div
                key={`${img.src}-${i}`}
                data-testid={`gallery-image-${i}`}
                className="masonry-item group cursor-pointer overflow-hidden"
                onClick={() => openLightbox(i)}
              >
                <div className="relative overflow-hidden">
                  <img
                    src={img.src}
                    alt={img.alt}
                    className={`w-full ${heightClasses[i % heightClasses.length]} object-cover group-hover:scale-105 transition-transform duration-500`}
                  />
                  <div className="absolute inset-0 bg-navy/0 group-hover:bg-navy/40 transition-all duration-300 flex items-end">
                    <div className="p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <span className="font-body text-xs uppercase tracking-wider text-white bg-crimson px-3 py-1">
                        {img.category}
                      </span>
                      <p className="font-body text-sm text-white mt-2">{img.alt}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightbox.open && (
        <div
          data-testid="lightbox-overlay"
          className="lightbox-overlay"
          onClick={closeLightbox}
        >
          <button
            data-testid="lightbox-close-btn"
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white hover:text-crimson-light transition-colors z-10"
          >
            <X className="w-8 h-8" />
          </button>
          <button
            data-testid="lightbox-prev-btn"
            onClick={(e) => { e.stopPropagation(); navigate(-1); }}
            className="absolute left-4 md:left-8 text-white hover:text-crimson-light transition-colors z-10"
          >
            <ChevronLeft className="w-10 h-10" />
          </button>
          <button
            data-testid="lightbox-next-btn"
            onClick={(e) => { e.stopPropagation(); navigate(1); }}
            className="absolute right-4 md:right-8 text-white hover:text-crimson-light transition-colors z-10"
          >
            <ChevronRight className="w-10 h-10" />
          </button>
          <img
            src={filtered[lightbox.index]?.src}
            alt={filtered[lightbox.index]?.alt}
            className="max-w-[90vw] max-h-[85vh] object-contain"
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-image"
          />
          <div className="absolute bottom-6 text-center text-white">
            <p className="font-body text-sm">{filtered[lightbox.index]?.alt}</p>
            <p className="font-body text-xs text-white/60 mt-1">
              {lightbox.index + 1} / {filtered.length}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
